import { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Switch } from '../../../components/ui/switch';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, RefreshCw, Settings, BookOpen, Lightbulb } from 'lucide-react';

const CATEGORIES = [
  { code: 'AP', name: 'AP', type: 'liability' },
  { code: 'BANK_FEES', name: 'Bank Fees', type: 'expense' },
  { code: 'BILLING_AGENT', name: 'Billing Agent', type: 'expense' },
  { code: 'CLEANING', name: 'Cleaning', type: 'expense' },
  { code: 'CRA_2023', name: 'CRA Payment - 2023 Accr', type: 'expense' },
  { code: 'CRA_RP_2025', name: 'CRA RP - 2025', type: 'expense' },
  { code: 'DELIVERY', name: 'Delivery', type: 'expense' },
  { code: 'DOCTOR_CARE', name: 'Doctor Care', type: 'expense' },
  { code: 'EMR', name: 'EMR', type: 'expense' },
  { code: 'FACILITY_MAINT', name: 'Facility Maintenance', type: 'expense' },
  { code: 'FOOD_ENT', name: 'Food and Entertainment', type: 'expense' },
  { code: 'INSURANCE', name: 'Insurance', type: 'expense' },
  { code: 'IT_SERVICES', name: 'IT Services', type: 'expense' },
  { code: 'LOAN_REPAY', name: 'Loan Repayment', type: 'liability' },
  { code: 'LOAN_GSAMRA', name: 'Loan Repayment - Gsamra', type: 'liability' },
  { code: 'LOAN_GMG_CONST', name: 'Loan to GMG - Const', type: 'asset' },
  { code: 'LOAN_GMG_ZEBO', name: 'Loan to GMG - ZebOville', type: 'asset' },
  { code: 'LOAN_PHARMACY', name: 'Loan to Pharmacy', type: 'asset' },
  { code: 'MISC', name: 'Misc', type: 'expense' },
  { code: 'PAYROLL_EMP', name: 'Payroll - Employee', type: 'expense' },
  { code: 'PHONE_INTERNET', name: 'Phone & Internet', type: 'expense' },
  { code: 'RENT', name: 'Rent', type: 'expense' },
  { code: 'SUPPLIES', name: 'Supplies', type: 'expense' },
  { code: 'TRAVEL', name: 'Travel', type: 'expense' },
  { code: 'UTILITIES', name: 'Utilities', type: 'expense' },
  { code: 'REV_DOCTORS', name: 'Revenue - Doctors', type: 'revenue' },
  { code: 'REV_UNINSURED', name: 'Revenue - Uninsured', type: 'revenue' },
  { code: 'BANK_CLEARING', name: 'Bank / Cash Clearing', type: 'asset' },
];

export default function VendorMappings() {
  const { api } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [form, setForm] = useState({
    vendor_pattern: '',
    category_code: '',
    keywords: ''
  });

  useEffect(() => {
    fetchMappings();
  }, []);

  const fetchMappings = async () => {
    setLoading(true);
    try {
      const res = await api.get('/finance/vendor-mappings');
      setMappings(res.data);
    } catch (error) {
      console.error('Failed to fetch mappings:', error);
      toast.error('Failed to load vendor mappings');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.vendor_pattern || !form.category_code) {
      toast.error('Vendor pattern and category are required');
      return;
    }
    
    try {
      const formData = new FormData();
      formData.append('vendor_pattern', form.vendor_pattern);
      formData.append('category_code', form.category_code);
      if (form.keywords) {
        formData.append('keywords', form.keywords);
      }
      
      await api.post('/finance/vendor-mappings', formData);
      toast.success('Mapping created');
      setShowAddDialog(false);
      setForm({ vendor_pattern: '', category_code: '', keywords: '' });
      fetchMappings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create mapping');
    }
  };

  const handleUpdate = async () => {
    if (!editingMapping) return;
    
    try {
      await api.patch(`/finance/vendor-mappings/${editingMapping.id || editingMapping.vendor_pattern}`, {
        category_code: form.category_code,
        keywords: form.keywords ? form.keywords.split(',').map(k => k.trim()) : [],
        active: true
      });
      toast.success('Mapping updated');
      setShowEditDialog(false);
      setEditingMapping(null);
      fetchMappings();
    } catch (error) {
      toast.error('Failed to update mapping');
    }
  };

  const handleDelete = async (mapping) => {
    if (!confirm(`Delete mapping for "${mapping.vendor_pattern}"?`)) return;
    
    try {
      await api.delete(`/finance/vendor-mappings/${mapping.id || mapping.vendor_pattern}`);
      toast.success('Mapping deleted');
      fetchMappings();
    } catch (error) {
      toast.error('Failed to delete mapping');
    }
  };

  const openEditDialog = (mapping) => {
    setEditingMapping(mapping);
    setForm({
      vendor_pattern: mapping.vendor_pattern,
      category_code: mapping.category_code,
      keywords: mapping.keywords?.join(', ') || ''
    });
    setShowEditDialog(true);
  };

  const filteredMappings = mappings.filter(m => 
    !searchTerm || 
    m.vendor_pattern?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.category_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const learnedCount = mappings.filter(m => m.learned_from_bulk || !m.manual_entry).length;
  const manualCount = mappings.filter(m => m.manual_entry).length;

  return (
    <div className="space-y-4" data-testid="vendor-mappings">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h3 className="text-lg font-medium flex items-center gap-2">
            <Settings className="h-5 w-5" />
            Vendor Category Mappings
          </h3>
          <p className="text-sm text-slate-500">
            Configure how vendors are automatically categorized
          </p>
        </div>
        <Button onClick={() => setShowAddDialog(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Mapping
        </Button>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-3">
          <div className="flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-700">
              <p className="font-medium">How auto-categorization works:</p>
              <ul className="list-disc list-inside mt-1 text-blue-600">
                <li>When you categorize a transaction, the system learns the vendor-category mapping</li>
                <li>Future transactions from the same vendor will be auto-suggested with that category</li>
                <li>You can also manually add mappings here for common vendors</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats and Search */}
      <div className="flex flex-wrap justify-between items-center gap-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex gap-4 text-sm">
          <span>Total: <strong>{mappings.length}</strong></span>
          <span className="text-blue-600">Learned: <strong>{learnedCount}</strong></span>
          <span className="text-emerald-600">Manual: <strong>{manualCount}</strong></span>
        </div>
        <div className="flex gap-2">
          <Input
            placeholder="Search vendors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-[200px]"
          />
          <Button variant="outline" size="icon" onClick={fetchMappings}>
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mappings Table */}
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      ) : filteredMappings.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No vendor mappings yet</p>
          <p className="text-sm text-slate-400">Mappings will be created as you categorize transactions</p>
        </div>
      ) : (
        <div className="rounded-lg border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Vendor Pattern</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Keywords</TableHead>
                <TableHead>Source</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMappings.map((mapping, idx) => (
                <TableRow key={mapping.id || idx}>
                  <TableCell className="font-medium">{mapping.vendor_pattern}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{mapping.category_name}</Badge>
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm max-w-[150px] truncate">
                    {mapping.keywords?.join(', ') || '-'}
                  </TableCell>
                  <TableCell>
                    {mapping.learned_from_bulk ? (
                      <Badge className="bg-blue-100 text-blue-700">Learned (Bulk)</Badge>
                    ) : mapping.manual_entry ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Manual</Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700">Learned</Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-slate-500 text-sm">
                    {mapping.updated_at ? new Date(mapping.updated_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => openEditDialog(mapping)}
                        title="Edit"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={() => handleDelete(mapping)}
                        title="Delete"
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Add Mapping Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Vendor Mapping</DialogTitle>
            <DialogDescription>
              Create a rule to automatically categorize transactions from this vendor.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor Pattern *</Label>
              <Input
                placeholder="e.g., BELL CANADA, AMAZON, etc."
                value={form.vendor_pattern}
                onChange={(e) => setForm({ ...form, vendor_pattern: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Transactions containing this text will be matched
              </p>
            </div>
            <div>
              <Label>Category *</Label>
              <Select 
                value={form.category_code} 
                onValueChange={(v) => setForm({ ...form, category_code: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.code}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Keywords (optional)</Label>
              <Input
                placeholder="e.g., phone, internet, telecom"
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              />
              <p className="text-xs text-slate-500 mt-1">
                Comma-separated keywords for additional matching
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreate}>Create Mapping</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Mapping Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Vendor Mapping</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Vendor Pattern</Label>
              <Input
                value={form.vendor_pattern}
                disabled
                className="bg-slate-50"
              />
            </div>
            <div>
              <Label>Category</Label>
              <Select 
                value={form.category_code} 
                onValueChange={(v) => setForm({ ...form, category_code: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.code} value={cat.code}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Keywords</Label>
              <Input
                placeholder="e.g., phone, internet, telecom"
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdate}>Save Changes</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
